import React, { useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import {
  components,
  InputActionMeta,
  SingleValue,
  SingleValueProps,
} from 'react-select';
import { useLazyQuery } from '@apollo/client';
import { GET_NAMED_BUCKET_DEFS } from '../../../../../pages/Formulas/apollo';
import {
  BucketKind,
  INamedBucketDefFieldsFragment,
  INamedBucketDefsQuery,
  INamedBucketDefsQueryVariables,
} from '../../../../../utils/apollo/graphqlSchema';
import { ReactSelectMenu } from '../../../../Dropdown/StyledReactSelect';
import { VersionedEntity } from '../../../../EntityLabels';
import BuilderInfo from '../../../../BuilderInfo';

interface IProps {
  onChange: (namedBucketDef: INamedBucketDefFieldsFragment) => void;
  onClear: () => void;
  selectedKind: BucketKind;
  activeSchemaId: string;
  selectedFieldKey: string | null;
  cachedNamedBucketDef: INamedBucketDefFieldsFragment | null;
  isDisabled: boolean;
}

export default ({
  onChange,
  onClear,
  selectedKind,
  activeSchemaId,
  selectedFieldKey,
  cachedNamedBucketDef,
  isDisabled,
}: IProps) => {
  /* Track current named bucket def */
  const [namedBucketDef, setNamedBucketDef] =
    useState<INamedBucketDefFieldsFragment | null>(cachedNamedBucketDef);

  const [loadNamedBucketDefs, { loading, data, refetch, called }] =
    useLazyQuery<INamedBucketDefsQuery, INamedBucketDefsQueryVariables>(
      GET_NAMED_BUCKET_DEFS,
      {
        variables: {
          term: '',
          includes: [],
          kinds: [selectedKind],
          schemaId: activeSchemaId,
          field: selectedFieldKey,
        },
      },
    );

  const options: INamedBucketDefFieldsFragment[] = useMemo(() => {
    const entities = data?.namedBucketDefs?.entities;

    if (!entities?.length) {
      return [];
    }

    return entities.sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const handleChange = (
    newOption: SingleValue<INamedBucketDefFieldsFragment>,
  ) => {
    setNamedBucketDef(newOption);

    if (!newOption) {
      onClear();

      return;
    }

    onChange(newOption);
  };

  // This callback would be fired even when input hasn't changed
  const handleSearch = debounce((term: string, actionMeta: InputActionMeta) => {
    // Using actionMeta to detect cases to avoid refetch when:
    // 1. Input hasn't been changed and user opens-closes menu;
    // 2. Input hasn't been changed and user selects an option;
    if (actionMeta.action === 'menu-close' && !actionMeta.prevInputValue) {
      return;
    }

    refetch({ term });
  }, 200);

  const renderLabel = (
    props: SingleValueProps<INamedBucketDefFieldsFragment, false>,
  ) => {
    const { data } = props;

    if (!data?.id) {
      return (
        <components.SingleValue {...props}>
          {props.children}
        </components.SingleValue>
      );
    }

    return (
      <components.SingleValue {...props}>
        <VersionedEntity
          onClear={props.clearValue}
          label={data.name}
          isDisabled={data.isDisabled}
          isLatestVersion={data.isLatestVersion}
        >
          <BuilderInfo kind="namedBucketDef" namedBucketDef={data} />
        </VersionedEntity>
      </components.SingleValue>
    );
  };

  const handleLoadOptions = () => {
    if (!called && !isDisabled) {
      loadNamedBucketDefs();
    }
  };

  return (
    <ReactSelectMenu
      isMulti={false}
      options={options}
      value={namedBucketDef}
      onChange={handleChange}
      onInputChange={handleSearch}
      onMenuOpen={handleLoadOptions}
      getOptionLabel={(option) => option.name}
      getOptionValue={(option) => option.id}
      components={{ SingleValue: renderLabel }}
      hideSelectedOptions
      isLoading={loading}
      isDisabled={isDisabled}
    />
  );
};
